import type { ScoreState } from '@aiscoreboard/contracts';
import { TrophyIcon } from './Icons';
import { accentOf, QUIET_FRAME } from '../lib/accent';
import { useT } from '../lib/i18n';

interface Props {
  state: ScoreState;
  className?: string;
  /** Drawn without glass of its own, because it shares a card with the break. */
  flat?: boolean;
}

/**
 * The match score: frames won, both players, on one line between them.
 *
 * It used to be a small box inside each player's panel, which is two halves of
 * one number kept two feet apart — nobody reads "2" on the left and "1" on the
 * right and thinks *2–1*, they read two unrelated figures and do the
 * arithmetic. A frame score is a pair, and a pair belongs side by side.
 *
 * It sits at the top of the middle column, level with the two name plates,
 * because that is the row the eye already treats as "who is playing". Whose
 * digit is whose is answered by colour rather than by position: the left one
 * is blue because the left player is blue, everywhere, all match.
 *
 * The caption above it is the format — *best of 3*, under a trophy — rather
 * than the word "Frames". Two seven-segment digits with a dash between them
 * are not ambiguous; nobody has ever needed to be told that the match score is
 * the match score. What they do need is the number it is being played to,
 * because 1–1 means nothing until you know whether the next frame ends it.
 * The format is also the only fact on this board that cannot change during a
 * frame, which is why it can be spent as a heading.
 */
export function FramesBlock({ state, className = '', flat = false }: Props) {
  const t = useT();
  const [a, b] = state.framesWon;
  const leader = a === b ? null : a > b ? 0 : 1;
  // A best-of-35 reaches double figures, and four digits plus a colon do not
  // fit the middle column at the size two digits do. The width term is scaled
  // by how many digits are actually on screen rather than being set low enough
  // for a case that almost never happens.
  const digits = `${a}`.length + `${b}`.length;
  const size = digits > 2 ? 'min(52cqh, 23cqw)' : 'min(52cqh, 42cqw)';

  return (
    <div
      className={
        flat
          ? `fit-h flex h-full w-full flex-col items-center justify-center gap-1.5 px-3 ${className}`
          : `hud-frame hud-frame-sm flex w-full shrink-0 flex-col items-center gap-1.5
             px-3 py-2 ${className}`
      }
      style={flat ? undefined : QUIET_FRAME}
    >
      <span className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        <TrophyIcon
          className={flat ? 'h-5 w-5 text-sys-yellow/60' : 'h-3.5 w-3.5 text-sys-yellow/55'}
        />
        {/* The format is the one heading on this board that is worth reading
            from across the room — 1–1 means nothing until you know whether the
            next frame ends it — so on the board it is set half again the size
            of every other caption rather than at the standard label size. */}
        <span className={`hud-label ${flat ? 'text-[1.05rem] tracking-[0.06em] text-white/75' : ''}`}>
          {t('match.bestOf', { n: state.bestOf })}
        </span>
      </span>
      {/* The row carries the digit size, and everything in it is set in `em`
          off that. Two things need it. The colon has to scale with the
          numerals rather than with the root, and — the reason it is worth the
          indirection — a row of numerals sitting on its baseline leaves the
          whole descender depth empty underneath, so a `justify-center` box
          puts the digits visibly above its middle. Nudging down by a fixed
          fraction of the *digit* size cancels that at every block height. */}
      <div
        className="flex w-full items-baseline justify-center gap-3 sm:gap-4"
        style={
          flat ? { fontSize: size, transform: 'translateY(0.07em)' } : undefined
        }
      >
        <Half value={a} player={0} lit={leader !== 1} flat={flat} />
        <span
          aria-hidden
          className={
            flat
              ? 'font-light leading-none text-white/35'
              : 'text-4xl font-light leading-none text-white/35 sm:text-5xl'
          }
          style={{
            transform: 'translateY(-0.08em)',
            ...(flat ? { fontSize: '0.64em' } : null),
          }}
        >
          :
        </span>
        <Half value={b} player={1} lit={leader !== 0} flat={flat} />
      </div>
    </div>
  );
}

/**
 * A trailing player's frames are dimmed rather than hidden. Both numbers are
 * facts and both stay legible; the brightness is only there so a glance from
 * the far side of a room lands on the one that is ahead.
 *
 * Plain numerals, set light and large, with a colon between them. There used
 * to be seven-segment digits here, on the argument that a settled count is a
 * different kind of quantity from points still in play and could say so in a
 * different display. In practice one segmented readout on a page of ordinary
 * numerals does not read as a distinction, it reads as a leftover from another
 * design — and at the size this block gets, the segments of a `1` are so far
 * apart that the digit stops looking like a number at all.
 */
function Half({
  value,
  player,
  lit,
  flat = false,
}: {
  value: number;
  player: 0 | 1;
  lit: boolean;
  flat?: boolean;
}) {
  return (
    <span
      className={`num-lg leading-none ${flat ? '' : 'text-6xl sm:text-7xl'} ${
        lit ? '' : 'opacity-40'
      }`}
      style={{
        color: accentOf(player).hex,
        ...(flat ? { fontSize: '1em' } : null),
      }}
    >
      {value}
    </span>
  );
}
